'use client';

import * as React from 'react';
import { AlertModal } from '@/components/shared/modals';
import { CancelButton, CTAButton } from '@/components/ui/buttons';
import { cn } from '@/lib/utils';

/**
 * Website Visit Method Selection Modal
 *
 * Allows users to choose between:
 * - Visited before: Include companies and people who already visited
 * - Visit from now on: Automatically add companies and people as they visit
 *
 * Migrated from legacy Modal to AlertDialog for consistency.
 */

interface WebsiteVisitMethodModalProps {
  /**
   * Whether the modal is open
   * @deprecated Use `open` prop instead
   */
  isOpen?: boolean;
  /**
   * Standardized prop name (preferred)
   */
  open?: boolean;
  /**
   * Callback when modal is closed
   * @deprecated Use `onOpenChange` prop instead
   */
  onClose?: () => void;
  /**
   * Standardized callback (preferred)
   */
  onOpenChange?: (open: boolean) => void;
  onSelect: (method: 'visited_before' | 'visit_from_now_on') => void;
}

export const WebsiteVisitMethodModal: React.FC<WebsiteVisitMethodModalProps> = ({
  isOpen, // Legacy prop
  open, // Standardized prop
  onClose, // Legacy prop
  onOpenChange, // Standardized prop
  onSelect,
}) => {
  const [selectedMethod, setSelectedMethod] = React.useState<'visited_before' | 'visit_from_now_on' | null>(null);

  // Support both legacy and new prop names
  const modalOpen = open !== undefined ? open : (isOpen ?? false);
  const handleOpenChange = onOpenChange || ((newOpen: boolean) => {
    if (!newOpen && onClose) {
      onClose();
    }
  });

  // Reset state when modal closes
  React.useEffect(() => {
    if (!modalOpen) {
      setSelectedMethod(null);
    }
  }, [modalOpen]);

  const handleSelect = (method: 'visited_before' | 'visit_from_now_on') => {
    setSelectedMethod(method);
  };

  const handleContinue = () => {
    if (selectedMethod) {
      onSelect(selectedMethod);
      handleOpenChange(false);
      setSelectedMethod(null);
    }
  };

  const handleCancel = () => {
    handleOpenChange(false);
    setSelectedMethod(null);
  };

  return (
    <AlertModal
      open={modalOpen}
      onOpenChange={handleOpenChange}
      title="Website visits audience"
      description="Which companies and people should be included?"
      size="lg"
      footer={
        <>
          <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          <CTAButton
            disabled={!selectedMethod}
            onClick={handleContinue}
          >
            Continue to rules
          </CTAButton>
        </>
      }
    >

      <div className="space-y-3">
        {/* Visited Before Option */}
        <div
          className={cn(
            'flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all border-2',
            selectedMethod === 'visited_before'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          )}
          onClick={() => handleSelect('visited_before')}
        >
          <div className="flex-shrink-0 mt-0.5">
            <div
              className={cn(
                'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                selectedMethod === 'visited_before'
                  ? 'border-blue-500'
                  : 'border-gray-300'
              )}
            >
              {selectedMethod === 'visited_before' && (
                <div className="w-3 h-3 rounded-full bg-blue-500" />
              )}
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              Visited before
            </h3>
            <p className="text-sm text-gray-600">
              Include all companies and people who already visited your website in the past
            </p>
          </div>
        </div>

        {/* Visit From Now On Option */}
        <div
          className={cn(
            'flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-all border-2',
            selectedMethod === 'visit_from_now_on'
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
          )}
          onClick={() => handleSelect('visit_from_now_on')}
        >
          <div className="flex-shrink-0 mt-0.5">
            <div
              className={cn(
                'w-5 h-5 rounded-full border-2 flex items-center justify-center',
                selectedMethod === 'visit_from_now_on'
                  ? 'border-blue-500'
                  : 'border-gray-300'
              )}
            >
              {selectedMethod === 'visit_from_now_on' && (
                <div className="w-3 h-3 rounded-full bg-blue-500" />
              )}
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-gray-900 mb-1">
              Visit from now on
            </h3>
            <p className="text-sm text-gray-600">
              Automatically add companies and people as they visit your website going forward
            </p>
          </div>
        </div>
      </div>
    </AlertModal>
  );
};
